import type { ReactNode } from "react";
import { KeyRound, LockKeyhole, Mail, Smartphone } from "lucide-react";

import { LoginChallenge } from "../../../backend/security";
import { formatRemaining, useLoginMfaChallenge } from "../../../components/auth/useLoginMfaChallenge";
import { Button } from "@/v2/ui/button";
import { Input } from "@/v2/ui/input";
import { Field, FieldError, FieldLabel } from "@/v2/ui/field";
import { AuthLink, Notice, PasswordField, SubmitButton } from "./parts";

/** Proving it's you after the email: a passkey, an authenticator code, an emailed code or the password */
export function MfaChallenge({ challenge, onResult, onCancel, onAbandon, preferPasskey, cancelLabel = "Use a different account" }: {
    challenge: LoginChallenge;
    onResult: (result: any) => Promise<void>;
    onCancel: () => void;
    /** The transaction ended; the login page shows why above the email form */
    onAbandon: (message: string, description: string) => void;
    /** Open the passkey first; see useLoginMfaChallenge */
    preferPasskey?: boolean;
    cancelLabel?: string;
}) {
    const {
        active, methods, requiresChoice, currentMethod, selectedMethod, setSelectedMethod,
        code, setCode, password, setPassword, error, setError, loading, secondsLeft, showExpiry, passkeySupported,
        sendEmailCode, verifyTotp, verifyPasskey, verifyPassword, chooseAnotherWay, alternative, switchTo,
    } = useLoginMfaChallenge({ challenge, onResult, onCancel, onAbandon, preferPasskey });

    const expiry = showExpiry && (
        <p className="text-center text-xs text-text-3">This sign-in request expires in {formatRemaining(secondsLeft!)}</p>
    );
    const back = (
        <div className="mt-2 flex flex-col items-center gap-2">
            {requiresChoice && selectedMethod && <LinkButton onClick={chooseAnotherWay}>Sign in another way</LinkButton>}
            <LinkButton onClick={onCancel}>{cancelLabel}</LinkButton>
        </div>
    );
    const passkeyButton = (
        <SubmitButton type="button" busy={loading} disabled={!passkeySupported} onClick={verifyPasskey}>
            <KeyRound /> Continue with a passkey
        </SubmitButton>
    );
    const noPasskeys = !passkeySupported && <p className="text-xs text-text-3">This browser can't use passkeys.</p>;

    if (requiresChoice && !selectedMethod) {
        const passkey = methods.find(method => method.type === "passkey");
        const authenticator = methods.find(method => method.type === "totp");
        const passwordMethod = methods.find(method => method.type === "password");
        const emailMethod = methods.find(method => method.type === "email");
        return (
            <div className="flex flex-col gap-3">
                {error && <Notice title={error} />}
                {/* Selecting the passkey starts the browser prompt in the same click.
                    A confirmation screen in between would only repeat this label. */}
                {passkey && passkeyButton}
                {passkey && noPasskeys}
                {authenticator && <MethodButton icon={<Smartphone />} disabled={loading} onClick={() => setSelectedMethod(authenticator)}>Use an authenticator app</MethodButton>}
                {passwordMethod && <MethodButton icon={<LockKeyhole />} disabled={loading} onClick={() => setSelectedMethod(passwordMethod)}>Use your password</MethodButton>}
                {emailMethod && <MethodButton icon={<Mail />} disabled={loading} onClick={sendEmailCode}>Email me a code</MethodButton>}
                {expiry}
                {back}
            </div>
        );
    }

    if (currentMethod?.type === "passkey") {
        return (
            <div className="flex flex-col gap-3">
                {error && <Notice title={error} />}
                {passkeyButton}
                {noPasskeys}
                {loading && <p className="text-center text-xs text-text-3">Use Face ID, Touch ID, your screen lock or security key when your browser asks.</p>}
                {expiry}
                <div className="mt-2 flex flex-col items-center gap-2">
                    {alternative && <LinkButton onClick={() => switchTo(alternative)}>Use your password instead</LinkButton>}
                    {requiresChoice && selectedMethod && <LinkButton onClick={chooseAnotherWay}>Sign in another way</LinkButton>}
                    <LinkButton onClick={onCancel}>{cancelLabel}</LinkButton>
                </div>
            </div>
        );
    }

    if (currentMethod?.type === "password") {
        return (
            <form noValidate className="flex flex-col gap-5" onSubmit={e => { e.preventDefault(); verifyPassword(); }}>
                <PasswordField
                    label="Password"
                    autoComplete="current-password"
                    autoFocus
                    value={password}
                    onChange={value => { setPassword(value); setError(undefined); }}
                    error={error}
                    aside={<AuthLink to={`/auth/reset-password${active.username ? `?email=${encodeURIComponent(active.username)}` : ""}`}>Forgot password?</AuthLink>}
                />
                <div className="flex flex-col gap-3">
                    <SubmitButton busy={loading} disabled={!password}>Sign in</SubmitButton>
                    {alternative && (
                        <Button type="button" variant="outline" disabled={loading} onClick={() => switchTo(alternative)} className="h-10 w-full rounded-xl text-sm">
                            <KeyRound /> Sign in with a passkey
                        </Button>
                    )}
                </div>
                {expiry}
                {back}
            </form>
        );
    }

    if (currentMethod?.type === "totp" || currentMethod?.type === "email") {
        const label = currentMethod.type === "email"
            ? "Six-digit code we emailed you"
            : "Six-digit code from your authenticator app";
        return (
            <form noValidate className="flex flex-col gap-5" onSubmit={e => { e.preventDefault(); verifyTotp(); }}>
                <Field data-invalid={!!error || undefined} className="gap-1.5">
                    <FieldLabel htmlFor="mfa-code">{label}</FieldLabel>
                    {/* One field rather than six boxes, so paste and the browser's one-time-code fill just work */}
                    <Input
                        id="mfa-code"
                        autoFocus
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        aria-invalid={!!error || undefined}
                        value={code}
                        onChange={e => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(undefined); }}
                        className="h-12 text-center font-mono text-xl tracking-[0.5em] md:text-xl"
                    />
                    {error && <FieldError className="text-xs text-bad-ink">{error}</FieldError>}
                </Field>
                <SubmitButton busy={loading} disabled={code.length !== 6}>Sign in</SubmitButton>
                {expiry}
                {back}
            </form>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            <Notice title="No supported sign-in method is available">Go back and use Google, GitHub, or another account.</Notice>
            <Button variant="outline" className="h-10 rounded-xl" onClick={onCancel}>Back</Button>
        </div>
    );
}

function MethodButton({ icon, children, disabled, onClick }: { icon: ReactNode; children: ReactNode; disabled?: boolean; onClick: () => void }) {
    return (
        <Button variant="outline" disabled={disabled} onClick={onClick} className="h-11 w-full justify-start gap-3 rounded-xl px-4 text-sm [&_svg]:text-text-2">
            {icon}
            {children}
        </Button>
    );
}

function LinkButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
    return (
        <button type="button" onClick={onClick} className="text-sm font-medium text-text-2 hover:text-text">
            {children}
        </button>
    );
}
